import { useEffect, useState, createContext, useContext, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const UnsavedChangesContext = createContext({
  hasUnsavedChanges: false,
  setUnsavedChanges: () => {},
  checkNavigation: () => true,
  setCustomDialog: () => {},
});

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);

export const UnsavedChangesProvider = ({ children }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [onDiscard, setOnDiscard] = useState(null);
  const [customDialog, setCustomDialogState] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const hasUnsavedRef = useRef(false);

  const setUnsavedChanges = useCallback((value, saveCallback, discardCallback) => {
    hasUnsavedRef.current = value;
    setHasUnsavedChanges(value);
    if (discardCallback) setOnDiscard(() => discardCallback);
  }, []);

  const setCustomDialog = useCallback((value) => {
    setCustomDialogState(value);
  }, []);

  const checkNavigation = useCallback((targetPath) => {
    if (hasUnsavedRef.current && targetPath !== location.pathname) {
      setPendingNavigation(targetPath);
      setShowDialog(true);
      return false;
    }
    return true;
  }, [location.pathname]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedRef.current) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleStay = () => {
    setShowDialog(false);
    setPendingNavigation(null);
  };

  const handleLeave = () => {
    if (onDiscard) onDiscard();
    setShowDialog(false);
    setHasUnsavedChanges(false);
    hasUnsavedRef.current = false;
    setCustomDialogState(null);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const isIframe = !!customDialog;

  return (
    <UnsavedChangesContext.Provider value={{
      hasUnsavedChanges,
      setUnsavedChanges,
      checkNavigation,
      setCustomDialog,
    }}>
      {children}
      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isIframe ? customDialog.title : "Cambios sin guardar"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isIframe ? customDialog.description : "Tienes cambios sin guardar. ¿Qué deseas hacer?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-2 sm:justify-end">
            {isIframe ? (
              <>
                <AlertDialogAction onClick={handleStay}>
                  Regresar
                </AlertDialogAction>
                <AlertDialogAction
                  onClick={handleLeave}
                  className="bg-red-500 hover:bg-red-600"
                >
                  Salir
                </AlertDialogAction>
              </>
            ) : (
              <>
                <AlertDialogAction
                  onClick={handleLeave}
                  className="bg-slate-500 hover:bg-slate-600"
                >
                  Descartar cambios
                </AlertDialogAction>
                <AlertDialogAction onClick={handleStay}>
                  Seguir editando
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </UnsavedChangesContext.Provider>
  );
};

// Componente wrapper para formularios normales
const UnsavedChangesGuard = ({ hasUnsavedChanges, onSave, onDiscard }) => {
  const { setUnsavedChanges } = useUnsavedChanges();

  useEffect(() => {
    setUnsavedChanges(hasUnsavedChanges, onSave, onDiscard);
    return () => setUnsavedChanges(false, null, null);
  }, [hasUnsavedChanges, onSave, onDiscard, setUnsavedChanges]);

  return null;
};

// Hook para iframes - llama una sola vez al montar
export const useIframeGuard = () => {
  const { setUnsavedChanges, setCustomDialog } = useUnsavedChanges();

  useEffect(() => {
    setUnsavedChanges(true);
    setCustomDialog({
      title: "Estás a punto de salir",
      description: "Asegúrate de guardar tus cambios antes de salir. Los datos no guardados se perderán.",
    });
    return () => {
      setUnsavedChanges(false);
      setCustomDialog(null);
    };
  }, [setUnsavedChanges, setCustomDialog]);
};

export default UnsavedChangesGuard;
